import * as React from "react";
import debounce from "lodash.debounce";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Skeleton from "@mui/material/Skeleton";
import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid } from "@mui/material";
import SearchListGrid from "../searchListGrid/SearchListGrid";
import CustomizedDialogs from '../../atoms/modal/customizedDialogs';
import { Button, Stack } from '@mui/material';
import { HeadCell, Order } from "../tableGrid/types";
import EnhancedTable from "../tableGrid/tableGrid";
import EnhancedTableToolbar from "../tableGrid/tableToolbar";
import EnhancedTableHead from "../tableGrid/tableHead";
import Row from "../tableGrid/tableRow";
import { useNavigate } from "react-router-dom";
import { fetchSpokesRequest, deleteSpokeReq } from "../../../store/spokes/actions";

import { ISpokeDetails, ISpokeSearchParams } from "../../../store/spokes/types";
import {
  fetchSpokePendingSelector,
  fetchSpokeSelector,
  deleteSpokePendingSelector,
  deleteSpokeSelector
} from "../../../store/spokes/selectors";
import { SEARCH_DEBOUNCE_TIME_MILLIS } from "../../../constants/common";

// settings data

const headCells: HeadCell[] = [
  {
    id: "name",
    numeric: false,
    disablePadding: false,
    label: "Name",
    width: "150px",
  },
  {
    id: "owner",
    numeric: false,
    disablePadding: false,
    label: "Owner",
    width: "230px",
  },
  {
    id: "adminGroupName",
    numeric: false,
    disablePadding: false,
    label: "Admin AD Group",
    width: "180px",
  },
  {
    id: "userGroupName",
    numeric: false,
    disablePadding: false,
    label: "User AD Group",
    width: "100px",
  },
];

const filterCells: string[] = ["name", "owner"];

// end

const descendingComparator = (
  a: ISpokeDetails,
  b: ISpokeDetails,
  orderBy: keyof ISpokeDetails
) => {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
};

const getComparator = (
  order: Order,
  orderBy: keyof ISpokeDetails
): ((a: ISpokeDetails, b: ISpokeDetails) => number) => {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

export type SpokeExplorerProps = {
  isAdmin: boolean;
}

const SpokeExplorer = (props: SpokeExplorerProps) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dataRows, setDataRows] = useState<ISpokeDetails[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof ISpokeDetails>("spokeId");
  const [selected, setSelected] = useState<readonly string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [open, setOpen] = useState(false);
  const [rowName, setRowName] = useState('');
  const [rowID, setRowID] = useState(0);
  const [deletedIds, setDeletedIds] = useState<Number[]>([]);
  const deletePending = useSelector(deleteSpokePendingSelector)
  const deleteStatus = useSelector(deleteSpokeSelector)
  const [deletedIdLoading, setDeletedIdLoading] = useState('')

  // Filters-related
  const [names, setNames] = useState<string[]>([]);
  const [owners, setOwners] = useState<string[]>([]);
  const [filters, setFilters] = useState<string[]>([]);
  const [openFilters, setOpenFilters] = useState(false);

  const selectable: string = "no";
  const allSelectable: boolean = false;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // source list

  const spokeList = useSelector(fetchSpokeSelector);
  const spokePendingList = useSelector(fetchSpokePendingSelector);

  const params: ISpokeSearchParams = {
    // skip: page * rowsPerPage,
    // top: rowsPerPage,
    // count: true,
    // search: searchTerm,
    // name: name,
    // owner: owner,
    count: true,
    top: rowsPerPage,
    skip: 0,
    name: [],
    owner: [],
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch(fetchSpokesRequest(params));
  }, [page, rowsPerPage, searchTerm]);

  useEffect(() => {
    let data: ISpokeDetails[] = spokeList;
    if (data != undefined) {
      for (let x = 0; x < data.length; x++) {
        data[x].createdAt = new Date(data[x].createdAt).toString();
      }
      setDataRows(data);
      setTotalRows(spokeList.length);
    }
  }, [spokeList]);

  // table related events

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: string
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property as keyof ISpokeDetails);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = dataRows!.map((n: { spokeId: any }) => n.spokeId);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const deleteAllClick = () => {
    setSelected([]);
  };

  const filterResults = (
    event: React.MouseEvent<HTMLButtonElement>,
    filters: string[]
  ) => {
    let newNames: string[] = [];
    let newOwners: string[] = [];

    filters.forEach((item) => {
      let filter: string[] = item.split(": ");
      switch (filter[0]) {
        case "name":
          newNames.push(filter[1]);
          setNames(newNames);
          params.name = newNames;
          break;
        case "owner":
          newOwners.push(filter[1]);
          setOwners(newOwners);
          params.owner = newOwners;
          break;
        default:
          break;
      }
    });

    setFilters(filters);
    setOpenFilters(true);
    setPage(0);

    dispatch(fetchSpokesRequest(params));
  };

  const refreshPage = () => {
    setNames([]);
    params.name = [];

    setOwners([]);
    params.owner = [];

    setFilters([]);
    setOpenFilters(false);

    dispatch(fetchSpokesRequest(params));
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPage(0);
    setSearchTerm(e.target.value);
  };

  const handleSearchDebounce = useMemo(() => {
    return debounce(handleSearch, SEARCH_DEBOUNCE_TIME_MILLIS);
  }, []);

  // const searchEl = () => {
  //   return (
  //     <TextField
  //       id="source-search"
  //       label="Search Spokes"
  //       type="search"
  //       fullWidth={true}
  //       onChange={handleSearchDebounce}
  //       InputProps={{
  //         startAdornment: (
  //           <InputAdornment position="start">
  //             <SearchIcon />
  //           </InputAdornment>
  //         ),
  //       }}
  //     />
  //   );
  // };

  const handleSpokeView = (row: ISpokeDetails, event: React.MouseEvent<HTMLButtonElement>) => {
    const url = `/ViewSpoke/${row.spokeId}`;
    if (event.ctrlKey) {
      window.open(url, '_blank')
    }
    else {
      navigate(url);
    }
  };




  const handleClose = () => {
    setOpen(false);
  };

  const tableToolBar = () => {
    return (
      <EnhancedTableToolbar
        numSelected={selected.length}
        title={"Spokes"}
        headCells={headCells}
        filterCells={filterCells}
        deleteAll={deleteAllClick}
        filterResults={filterResults}
        refreshPage={refreshPage}
        currentFilters={filters}
        openToolbar={openFilters}
      />
    );
  };

  const tableHeader = () => {
    return (
      <EnhancedTableHead
        headCells={headCells}
        numSelected={selected.length}
        selectable={allSelectable}
        order={order}
        orderBy={orderBy}
        onSelectAllClick={handleSelectAllClick}
        onRequestSort={handleRequestSort}
        rowCount={totalRows}
      />
    );
  };

  const isSelected = (name: string) => selected.indexOf(name) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - totalRows) : 0;


  const confirmSpokeDelete = (row: ISpokeDetails) => {
    setOpen(true);
    setRowID(row.spokeId);
    setRowName(row.name);

  }

  const handleSpokeDelete = () => {
    setOpen(false);
    dispatch(deleteSpokeReq(rowID));
    setDeletedIds(item => [...item, rowID]);
    setDeletedIdLoading(rowID.toString())

  }


  const tableRows = () => {
    return (
      <>
        {dataRows !== undefined &&
          dataRows!.length > 0 &&
          dataRows!
            .slice()
            .sort(getComparator(order, orderBy))
            .map((row: ISpokeDetails, index: number) => {
              const isItemSelected = isSelected(row.name);

              return (
                <Row
                  row={row}
                  key={row.spokeId}
                  headCells={headCells}
                  selectable={selectable}
                  canEdit={false}
                  canView={true}
                  canDelete={props.isAdmin ? true : false}
                  canExpand={false}
                  isItemSelected={isItemSelected}
                  handleView={handleSpokeView}
                  handleDelete={confirmSpokeDelete}
                  loadingIcon={deletePending}
                  deletedRows={deletedIds}
                  deletedIdLoading={deletedIdLoading}
                  deleteId={deleteStatus}
                ></Row>
              );
            })}
        {emptyRows > 0 && emptyRows < 10 && (
          <TableRow
            style={{
              height: 53 * emptyRows,
            }}
          >
            <TableCell colSpan={12} />
          </TableRow>
        )}
      </>
    );
  };

  const listGrid = () => {
    return (
      <>
        {/* adding skeleton screen on loading */}
        {spokePendingList ? (
          <Grid container width="100%">
            <Grid item xs={12}>
              <Skeleton animation="wave" height={60} />
            </Grid>
            <Grid item xs={12}>
              <Skeleton animation="wave" height={60} />
            </Grid>
            <Grid item xs={12}>
              <Skeleton animation="wave" height={60} />
            </Grid>
            <Grid item xs={12}>
              <Skeleton animation="wave" height={60} />
            </Grid>
            <Grid item xs={12}>
              <Skeleton animation="wave" height={60} />
            </Grid>
            <Grid item xs={12}>
              <Skeleton animation="wave" height={60} />
            </Grid>
            <Grid item xs={12}>
              <Skeleton animation="wave" height={60} />
            </Grid>
            <Grid item xs={12}>
              <Skeleton animation="wave" height={60} />
            </Grid>
          </Grid>
        ) : (
          <EnhancedTable
            totalRows={totalRows}
            tableToolBar={tableToolBar()}
            tableHeader={tableHeader()}
            tableRows={tableRows()}
            pagination={true}
            page={page}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
          />
        )}
      </>
    );
  };

  return (
    <>
      <SearchListGrid searchEl={<></>} children={listGrid()} />
      <CustomizedDialogs
        dialogHeight=''
        modalSize="md"
        key="target"
        modalTitle="Delete Confirmation"
        isOpen={open}
        onClose={handleClose}
        footerAction={
          <Stack direction="row" spacing={2}>
            <Button id="6dbed77e-20f1-4219-aab8-3203dac924ac" color="primary" onClick={handleSpokeDelete} autoFocus>
              Yes
            </Button>
            <Button id="71989911-c30c-4c06-81d8-a354927915a0" color="primary" onClick={handleClose}>
              No
            </Button>
          </Stack>
        }
      >
        <> Are you sure you want to delete {rowName}?</>
      </CustomizedDialogs>
    </>
  );
};

export default SpokeExplorer;
